import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, ElementRef, forwardRef, Input } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QuestionType, AnimationOption } from 'library-explorer';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { QuestionFillBlanksStyle } from 'src/app/model/enums/question-fill-blanks-style.enum';

@Component({
    selector: 'app-question-fill-blank',
    templateUrl: './question-fill-blank.component.html',
    styleUrls: ['./question-fill-blank.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuestionFillBlankComponent),
            multi: true
        }
    ],
    standalone: false
})
export class QuestionFillBlankComponent implements ControlValueAccessor {
  @Input() animation: AnimationOption = AnimationOption.NONE;
  @Input() animationDelay = 0;
  @Input() disabled = false;
  @Input() questionHash: string;

  @Input() public type: QuestionFillBlanksStyle = QuestionFillBlanksStyle.TYPE_IN;

  @Input() set availableValues(values: string[]) {
    if (!values) {
      this.options = [];
      return;
    }

    this.options = [].concat(...values.map(value => value.split('|'))).map((option, index) => ({
      text: option,
      id: index + 1
    }))
  }

  @Input() set text(value: string) {
    const div = document.createElement('div');
    div.innerHTML = value;

    for (const child of Array.from(div.children)) {
      const newItem: any = {
        nodeName: child.nodeName,
      };
      switch (child.nodeName) {
        case 'P':
          newItem.items = this.parseContent(child);
          break;
        case 'UL':
          newItem.rows = this.parseList(child);
          break;
        case 'OL':
          newItem.rows = this.parseList(child);
          break;
      }

      this.nodeList.push(newItem);
    }
  }

  @Input() set blankWidth(width: string) {
    if (!width) {
      return;
    }
  
    this.element.nativeElement.style.setProperty('--lms-fill-in-blank-width', width);
  }

  public readonly types: typeof QuestionFillBlanksStyle = QuestionFillBlanksStyle;

  public options: { id: number, text: string }[] = [];

  public activeOption: { id: number, text: string } = null;

  public nodeList: any[] = [];

  public formArray: FormArray;

  private readonly EMPTY_LINE_VALUE = '_____';
  private blanksCount = 0;

  public formValueSubscirption: Subscription;

  constructor(
    private readonly element: ElementRef,
    private readonly formBuilder: FormBuilder) { }

  onChange = (data: any) => {};
  onTouched = () => {};

  public valueChanged(): void {
    const values = this.formArray.value;
    const filledValues = values.map(item => item?.text).filter(item => item);

    if (filledValues.length !== this.blanksCount) {
      this.onChange(null);

      return;
    }

    this.onChange({
      type: QuestionType.FILL_BLANKS,
      answer: filledValues,
      questionHash: this.questionHash
    });
  }

  public writeValue(obj: any): void { }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public setActiveOption(option: { id: number, text: string } = null): void {
    this.activeOption = option;
  }

  public dropActiveOptionToContainer(containerIndex: number): void {
    const currentControl = this.formArray.at(containerIndex);
    const containerValue = currentControl?.value?.id ? currentControl?.value: null;

    if (!this.activeOption?.id) {
      this.activeOption = containerValue;
      return;
    }

    const prevControl = this.formArray.controls.find(control => control?.value?.id === this.activeOption?.id);
    currentControl.patchValue(this.activeOption);

    this.handleOptionDropAndUpdatePrevContainer(prevControl, this.activeOption.id, containerValue);
    this.activeOption = null;
  }

  public drop(event: CdkDragDrop<any>): void {
    this.activeOption = null;
    const fromContainerIndex = event.previousContainer.data?.index ?? -1;
    const toContainerIndex = event.container.data?.index ?? -1;
    const data = event.item.data;

    if (fromContainerIndex === toContainerIndex) {
      return;
    }

    const currentControl = toContainerIndex > -1 && this.formArray.at(toContainerIndex);
    const prevControl = fromContainerIndex > -1 && this.formArray.at(fromContainerIndex);
    const formerValue = currentControl?.value?.id ? currentControl?.value: null;

    if (toContainerIndex !== -1) {     
      currentControl?.patchValue(data);
    } else {
      this.options.unshift(data);
    }

    this.handleOptionDropAndUpdatePrevContainer(prevControl, data?.id, formerValue);
  }

  private handleOptionDropAndUpdatePrevContainer(prevControl: AbstractControl, optionId: number, prevOption: { id: number, text: string}): void {
    if (prevControl) {
      prevControl.patchValue(prevOption || { id: null, text: null });
      return;
    }

    prevOption?.id && this.options.push(prevOption);
    this.options = this.options?.filter(opt => opt.id !== optionId);
  }

  private parseContent(element): void {
    let content = element.textContent || element.innerText;
    let blanksCounter = 0;

    content = content.replace(new RegExp(this.EMPTY_LINE_VALUE, 'g'), ` ${this.EMPTY_LINE_VALUE} `);
    content = content.replace(/[\r\n]+/g, ' \n ');
    content = content.split(' ').filter(item => item);

    content = content.map(item => {
      if (item !== this.EMPTY_LINE_VALUE) {
        return ` ${item} `;
      }

      const blankItem = { isInput: true, index: this.blanksCount + blanksCounter };
      blanksCounter++;
  
      return blankItem;
    });

    this.blanksCount += blanksCounter;

    this.initFormArray();
    
    return content;
  }

  private initFormArray(): void {
    if (this.formValueSubscirption) {
      this.formValueSubscirption.unsubscribe();
    }

    const formControls = new Array(this.blanksCount).fill(null).map(() => {
      return this.formBuilder.group({
        id: '',
        text: ''
      });
    });
    
    this.formArray = this.formBuilder.array(formControls);

    this.formValueSubscirption = this.formArray.valueChanges
      .pipe(
        debounceTime(200)
      )
      .subscribe(() => this.valueChanged());
  }

  private parseList(element) {
    const rows: any[] = [];

    if (element.children) {
      for (const child of Array.from(element.children)) {
        rows.push({items: this.parseContent(child)});
      }
    }

    return rows;
  }

}
